import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import HeroSection from '../components/home/HeroSection';
import ProductCarousel from '../components/home/ProductCarousel';
import CategoryCarousel from '../components/home/CategoryCarousel';
import Features from '../components/home/Features';
import FourImageGrid from '../components/home/FourImageGrid';
import InstagramFeed from '../components/home/InstagramFeed';
import LoadingSpinner from '../components/common/LoadingSpinner';

const HomePage = () => {
  const [sections, setSections] = useState([]);
  const [sectionProducts, setSectionProducts] = useState({});
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSections = useCallback(async () => {
    try {
      const data = await api.get('/sections');
      setSections(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch sections', err);
      setError('Failed to load page content. Please refresh.');
      setSections([]);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const cats = await api.get('/categories');
      setAllCategories(cats);
    } catch (err) {
      console.error('Failed to fetch categories', err);
    }
  }, []);

  const fetchProductsForSections = useCallback(async (sectionsData) => {
    const productSections = sectionsData.filter(s => s.type === 'products' && s.enabled);
    for (const section of productSections) {
      try {
        const products = await api.get(`/sections/${section._id}/products`);
        setSectionProducts(prev => ({ ...prev, [section._id]: products }));
      } catch (err) {
        console.error(`Failed to load products for section ${section.name}`, err);
      }
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchSections();
      await fetchCategories();
      setLoading(false);
    };
    load();
  }, [fetchSections, fetchCategories]);

  useEffect(() => {
    if (sections.length) {
      fetchProductsForSections(sections);
    }
  }, [sections, fetchProductsForSections]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        setLoading(true);
        await fetchSections();
        await fetchCategories();
        setLoading(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchSections, fetchCategories]);

  const getViewAllLink = (section) => {
    const categoryId = section.settings?.viewAllCategoryId;
    if (!categoryId) return null;
    const category = allCategories.find(c => c._id === categoryId);
    if (!category) return null;
    return `/category/${category.slug}`;
  };

  const renderSection = (section) => {
    if (!section.enabled) return null;

    switch (section.type) {
      case 'hero':
        return <HeroSection key={section._id} />;

      case 'products': {
        const products = sectionProducts[section._id] || [];
        if (products.length === 0) return null;
        const viewAllLink = getViewAllLink(section);
        return (
          <ProductCarousel
            key={section._id}
            products={products}
            title={section.name}
            buttonText="VIEW ALL"
            viewAllLink={viewAllLink}
          />
        );
      }

      case 'categories': {
        let categoriesToShow = allCategories;
        if (section.settings?.categoryIds?.length) {
          categoriesToShow = allCategories.filter(c => section.settings.categoryIds.includes(c._id));
        }
        if (categoriesToShow.length === 0) return null;
        return (
          <CategoryCarousel
            key={section._id}
            categories={categoriesToShow}
          />
        );
      }

      case 'features':
        return <Features key={section._id} settings={section.settings || {}} />;

      case 'grid':
        return <FourImageGrid key={section._id} images={section.settings?.images || []} />;

      case 'instagram':
        return <InstagramFeed key={section._id} posts={section.settings?.posts || []} />;

      default:
        return null;
    }
  };

  if (loading) return <LoadingSpinner fullScreen text="Loading homepage..." />;
  if (error) return <div className="text-center py-20 text-red-600">{error}</div>;

  return (
    <div>
      {sections.sort((a, b) => a.order - b.order).map(section => renderSection(section))}
    </div>
  );
};

export default HomePage;